import { useEffect, useState } from 'react';
import { isForbidden, jobRoleErrorMessage } from '../jobRoleErrors';

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  // The API sends UTC instants without a zone designator ("2026-03-05T17:00:00"); read as UTC, not as the viewer's local time.
  const date = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export type Load<T> = { kind: 'loading' } | { kind: 'ready'; data: T } | { kind: 'forbidden' } | { kind: 'error'; message: string };

/** Loads one read for a tab, with the same four states every tab shows: loading, ready, no access (403) and a retryable error. */
export function useTabLoad<T>(load: (signal: AbortSignal) => Promise<T>, deps: unknown[], failure: string) {
  const [state, setState] = useState<Load<T>>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({ kind: 'loading' });
    load(controller.signal)
      .then((data) => { if (!controller.signal.aborted) setState({ kind: 'ready', data }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: jobRoleErrorMessage(err, failure) });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);
  return { state, retry: () => setNonce((n) => n + 1) };
}
